import { RouterButton } from 'components/Button/RouterButton';
import { Icon } from 'components/Icon';
import { BodySmall } from 'components/Text/BodySmall';
import { H5 } from 'components/Text/H5';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { showModal } from 'modules/main/store/ducks/modal.actions';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { listingSearchHomePath } from 'utils/searchHomePages';

import {
  CmsSocialSidebarContainer,
  CmsSocialSidebarRecentJobsContainer,
  CmsSocialSidebarTooltipContainer,
} from './CmsSocialSidebar.styles';

type Props = {
  headline?: string;
  description?: string;
  hasRecentJobs: boolean;
  hasSocialShare: boolean;
  subscribe: () => Promise<void>;
  canSubscribe: boolean;
  isSubscribed: boolean;
  shareUrl: Record<UserLocale, string>;
  subsiteSubscribeText: string;
};

export function CmsSocialSidebar({
  headline = getText('Land Your Dream Job'),
  canSubscribe,
  isSubscribed,
  subscribe,
  description = getText('Careers that help you move from intention to action'),
  hasRecentJobs,
  hasSocialShare,
  shareUrl,
  subsiteSubscribeText,
}: Props) {
  const dispatch = useMainDispatch();

  function handleShare() {
    dispatch(
      showModal('SHARE_LISTING', {
        listing: { type: 'URL' },
        listingType: 'URL',
        messageOverride: getText('Check out this article'),
        shareUrl,
      }),
    );
  }

  return (
    <CmsSocialSidebarContainer $hasRecentJobs={hasRecentJobs}>
      {hasRecentJobs ? (
        <CmsSocialSidebarRecentJobsContainer>
          <H5 lineHeight={1} mb="16px">
            {headline}
          </H5>
          <BodySmall lineHeight={1.2} mb="16px">
            {description}
          </BodySmall>
          <RouterButton
            data-qa-id="sidebar-recent-jobs-button"
            onClick={() => trackClicked('Search Jobs', { context: 'CMS Page' })}
            to={listingSearchHomePath('JOB')}
            variant="primary"
            size="small"
          >
            {getText('Search Jobs')}
          </RouterButton>
        </CmsSocialSidebarRecentJobsContainer>
      ) : null}
      <CmsSocialSidebarTooltipContainer>
        {canSubscribe ? (
          <Tooltip
            variant="filled"
            content={
              isSubscribed
                ? getText('You are now subscribed!')
                : subsiteSubscribeText
            }
          >
            <button
              type="button"
              onClick={subscribe}
              data-qa-id="sidebar-subscribe-to-subsite"
              disabled={isSubscribed}
              aria-label={getText('Subscribe')}
            >
              <Icon colorName="lightContentGrey" name="newsletter" size={30} />
            </button>
          </Tooltip>
        ) : null}
        {hasSocialShare ? (
          <Tooltip variant="filled" content={getText('Share')}>
            <button
              type="button"
              onClick={handleShare}
              data-qa-id="sidebar-social-share-button"
              aria-label={getText('Share')}
            >
              <Icon colorName="lightContentGrey" name="share" size={24} />
            </button>
          </Tooltip>
        ) : null}
      </CmsSocialSidebarTooltipContainer>
    </CmsSocialSidebarContainer>
  );
}
